import React, { useState } from "react";
import { Eye, Target, CheckCircle2, X, ZoomIn, BadgeCheck } from "lucide-react";
import {
  vizyonMisyon,
  kalitePolitikasi,
  personelPolitikasi,
  certificates,
} from "../mock";
import { Reveal, PageHero } from "../components/Shared";
import useSEO from "../hooks/useSEO";

const seoMap = {
  "vizyon-misyon": {
    title: "Vizyon & Misyon",
    description:
      "Nikomedya Turizm'in vizyonu ve misyonu: inovatif düşünce yapısıyla sektöre yön vermek, müşteri memnuniyetini beklentilerin ötesine taşımak.",
  },
  "kalite-politikamiz": {
    title: "Kalite Politikamız - ISO 9001, ISO 14001, OHSAS 18001",
    description:
      "Nikomedya Turizm kalite politikası ve belgelerimiz: ISO 9001:2008, ISO 14001:2004, OHSAS 18001:2007 ve mesleki yeterlilik belgeleri.",
  },
  "personel-politikamiz": {
    title: "Personel Politikamız",
    description:
      "Nikomedya Turizm personel politikası: eğitimli ve motive kadro, düzenli seminerler, uzun süreli çalışma ve takım ruhu prensibi.",
  },
};

/* ---------- Vizyon & Misyon ---------- */
const VizyonMisyon = () => (
  <div>
    <PageHero
      title="Vizyon & Misyon"
      subtitle="Bizi geleceğe taşıyan hedeflerimiz ve hizmet anlayışımızın temelleri."
    />
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <Reveal direction="left">
            <div className="feature-card h-full bg-[#f5f8fb] border border-slate-100 rounded-3xl p-10 lg:p-12">
              <div className="feature-icon w-16 h-16 rounded-2xl bg-white shadow-md text-[#1e9cd7] flex items-center justify-center">
                <Eye size={30} />
              </div>
              <h2 className="mt-7 text-2xl font-bold text-[#0d2b4f] tracking-tight">
                Vizyonumuz
              </h2>
              <p className="mt-5 text-[#3a4a5d] leading-[1.9] text-[16px]">
                {vizyonMisyon.vizyon}
              </p>
            </div>
          </Reveal>
          <Reveal direction="right">
            <div className="feature-card h-full bg-[#0d2b4f] rounded-3xl p-10 lg:p-12">
              <div className="w-16 h-16 rounded-2xl bg-[#1e9cd7]/15 text-[#1e9cd7] flex items-center justify-center">
                <Target size={30} />
              </div>
              <h2 className="mt-7 text-2xl font-bold text-white tracking-tight">
                Misyonumuz
              </h2>
              <p className="mt-5 text-white/70 leading-[1.9] text-[16px]">
                {vizyonMisyon.misyon}
              </p>
              <div className="mt-8 border-l-2 border-[#1e9cd7] pl-5">
                <p className="text-lg font-bold text-white italic leading-relaxed">
                  "{vizyonMisyon.misyonVurgu}"
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  </div>
);

/* ---------- Kalite Politikamız ---------- */
const KalitePolitikasi = () => {
  const [selected, setSelected] = useState(null);

  return (
    <div>
      <PageHero
        title="Kalite Politikamız"
        subtitle="Uluslararası kalite standartlarıyla belgelenmiş, taviz vermeyen hizmet anlayışı."
      />
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="max-w-3xl">
              <p className="section-label text-[#1e9cd7] text-[11px] font-bold uppercase mb-4">
                Kalite Anlayışımız
              </p>
              <p className="text-[#3a4a5d] leading-[1.9] text-lg">
                {kalitePolitikasi.intro}
              </p>
            </div>
          </Reveal>

          <Reveal>
            <h2 className="mt-20 mb-10 text-2xl sm:text-3xl font-bold text-[#0d2b4f] tracking-tight">
              Kalite Belgelerimiz
            </h2>
          </Reveal>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5">
            {certificates.map((c, i) => (
              <Reveal key={c.id} delay={i * 80}>
                <button
                  onClick={() => setSelected(c)}
                  className="news-card group block w-full text-left bg-white rounded-2xl overflow-hidden border border-slate-200"
                >
                  <div className="relative aspect-[3/4] overflow-hidden bg-slate-50">
                    <img
                      src={c.image}
                      alt={c.name}
                      loading="lazy"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-[#050e1d]/0 group-hover:bg-[#050e1d]/40 transition-colors duration-300 flex items-center justify-center">
                      <span className="w-12 h-12 rounded-full bg-white text-[#0d2b4f] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <ZoomIn size={20} />
                      </span>
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="text-sm font-bold text-[#0d2b4f] leading-snug">
                      {c.name}
                    </h3>
                    <p className="mt-1 text-xs text-[#5b6b7d] leading-relaxed">
                      {c.desc}
                    </p>
                  </div>
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {selected && (
        <div
          className="fixed inset-0 z-[100] bg-[#050e1d]/90 backdrop-blur-sm flex items-center justify-center p-4 sm:p-10"
          onClick={() => setSelected(null)}
        >
          <button
            className="absolute top-6 right-6 w-12 h-12 rounded-full bg-white/10 border border-white/20 text-white flex items-center justify-center hover:bg-white hover:text-[#0d2b4f] transition-colors"
            aria-label="Kapat"
          >
            <X size={22} />
          </button>
          <div
            className="max-h-full overflow-auto rounded-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={selected.image}
              alt={selected.name}
              className="max-h-[80vh] w-auto rounded-2xl shadow-2xl"
            />
            <p className="mt-4 text-center text-white font-bold">
              {selected.name}{" "}
              <span className="text-white/60 font-normal">— {selected.desc}</span>
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

/* ---------- Personel Politikamız ---------- */
const PersonelPolitikasi = () => (
  <div>
    <PageHero
      title="Personel Politikamız"
      subtitle="Eğitimli, motive ve kendini sürekli yenileyen kadromuzla hizmet kalitemizi güvence altına alıyoruz."
    />
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-center">
          <Reveal direction="left">
            <div className="space-y-6">
              {personelPolitikasi.paragraphs.map((p, i) => (
                <p key={i} className="text-[#3a4a5d] leading-[1.9] text-[16px]">
                  {p}
                </p>
              ))}
              <ul className="pt-4 space-y-4">
                {personelPolitikasi.principles.map((pr) => (
                  <li key={pr} className="flex items-start gap-3">
                    <CheckCircle2
                      size={20}
                      className="text-[#1e9cd7] shrink-0 mt-0.5"
                    />
                    <span className="text-[15px] font-medium text-[#0d2b4f]">
                      {pr}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal direction="right">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <img
                src="/images/personel.webp"
                alt="Nikomedya Turizm profesyonel kadro"
                loading="lazy"
                className="w-full h-[420px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050e1d]/80 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-8 flex items-center gap-4">
                <span className="w-14 h-14 rounded-full bg-[#1e9cd7] text-white flex items-center justify-center shrink-0">
                  <BadgeCheck size={24} />
                </span>
                <p className="text-white font-bold text-lg leading-snug">
                  Mesleki yeterlilik belgeli, deneyimli sürücü kadrosu
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  </div>
);

const KurumsalPage = ({ type }) => {
  const seo = seoMap[type] || seoMap["personel-politikamiz"];
  useSEO(seo);

  if (type === "vizyon-misyon") return <VizyonMisyon />;
  if (type === "kalite-politikamiz") return <KalitePolitikasi />;
  return <PersonelPolitikasi />;
};

export default KurumsalPage;
